import { useEffect, useState, useRef } from 'react';

type UseCountUpOptions = {
  start: number;
  end: number;
  duration?: number; // in milliseconds
};

export function useCountUp({
  start,
  end,
  duration = 1000,
}: UseCountUpOptions): [number, React.RefObject<HTMLDivElement>] {
  const [count, setCount] = useState<number>(start);
  const [hasStarted, setHasStarted] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const startTimestampRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Delay any browser-only logic until client hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Run IntersectionObserver only on the client
  useEffect(() => {
    if (!isHydrated || hasStarted || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [isHydrated, hasStarted]);

  // Animate counting up once triggered
  useEffect(() => {
    if (!hasStarted) return;

    const step = (timestamp: number) => {
      if (!startTimestampRef.current) {
        startTimestampRef.current = timestamp;
      }

      const progress = Math.min((timestamp - startTimestampRef.current) / duration, 1);
      const value = Math.floor(start + (end - start) * progress);
      setCount(value);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [hasStarted, start, end, duration]);

  return [count, ref];
}
